import React, { useEffect } from 'react';
import { Share2, PlusSquare, Smartphone, X, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function IosInstallPromptModal({ isOpen, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleShareClick = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'MUNDOXYZ',
          text: 'MundoXYZ - Tu plataforma de comercio y catálogo digital',
          url: window.location.origin
        });
      } catch (err) {
        if (err?.name !== 'AbortError') {
          toast('Pulsa el botón de Compartir en la barra de Safari');
        }
      }
    } else {
      toast('Pulsa el botón Compartir (icono cuadrado con flecha) en la barra de Safari');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 shadow-2xl p-5 sm:p-6 text-slate-800 dark:text-slate-100 transition-all my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón cerrar */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          title="Cerrar"
        >
          <X size={18} />
        </button>

        {/* Encabezado */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-600/30 shrink-0">
            <Smartphone className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-base font-extrabold leading-tight">
              Instalar en Pantalla de Inicio
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              iPhone & iPad (Safari)
            </p>
          </div>
        </div>

        {/* Explicación */}
        <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800/40 text-xs text-violet-900 dark:text-violet-200 mb-4 leading-relaxed">
          <p className="font-semibold mb-1">🔔 Para recibir Notificaciones Push en iOS:</p>
          <p className="text-[11px] opacity-90">
            Apple requiere que agregues MundoXYZ a tu pantalla de inicio. Una vez agregada, ¡podrás activar alertas inmediatas de ventas y mensajes!
          </p>
        </div>

        {/* Botón interactivo para abrir Compartir */}
        <button
          type="button"
          onClick={handleShareClick}
          className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-md shadow-violet-600/30 transition-all hover:scale-[1.02] active:scale-[0.98] mb-4 cursor-pointer"
        >
          <Share2 size={16} />
          <span>Abrir menú Compartir de Safari</span>
        </button>

        {/* Pasos visuales */}
        <div className="space-y-2.5 text-xs">
          <div className="flex items-start gap-2.5 p-2 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
            <span className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
              1
            </span>
            <div className="flex-1">
              <p className="font-bold text-[12px] flex items-center gap-1.5">
                Toca Compartir <Share2 size={13} className="text-violet-600 dark:text-violet-400 inline" />
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                En la barra inferior (o superior) de Safari, presiona el icono de compartir.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 p-2 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
            <span className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
              2
            </span>
            <div className="flex-1">
              <p className="font-bold text-[12px] flex items-center gap-1.5">
                "Agregar a inicio" <PlusSquare size={13} className="text-violet-600 dark:text-violet-400 inline" />
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Desliza las opciones y selecciona <strong>Agregar a pantalla de inicio</strong>.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 p-2 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
            <span className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
              3
            </span>
            <div className="flex-1">
              <p className="font-bold text-[12px] flex items-center gap-1.5">
                Toca "Agregar" y abre la app <CheckCircle size={13} className="text-emerald-500 inline" />
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Pulsa <strong>Agregar</strong> arriba a la derecha. Luego abre MundoXYZ desde tu pantalla de inicio para activar las notificaciones.
              </p>
            </div>
          </div>
        </div>

        {/* Botón cerrar */}
        <div className="mt-5 pt-3 border-t border-slate-100 dark:border-white/10 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 text-slate-700 dark:text-slate-200 font-bold text-xs transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
